import { useState, useEffect, type CSSProperties, type FormEvent, type ReactNode } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api.js';
import { Layout } from '../components/Layout.js';
import { Icon } from '../components/ui/Icon.js';
import { Chip } from '../components/ui/Chip.js';
import { Button } from '../components/ui/Button.js';
import { CUISINE_OPTIONS, DIETARY_RESTRICTION_OPTIONS } from '@meal-planner/shared';
import { useTheme, type Theme } from '../contexts/ThemeContext.js';
import { FamilyRosterField, type FamilyMember } from '../components/FamilyRosterField.js';
import { AllergenTrackerSection } from '../components/AllergenTrackerSection.js';

interface ProfileData {
  familySize: number;
  budget: number | null;
  dietaryRestrictions: string[];
  cuisinePreferences: string[];
  zipCode: string | null;
  equipment: string[];
  skillLevel: string;
  busyNightMode: boolean;
  familyMembers: FamilyMember[] | null;
  hiddenVeggieMode: boolean;
  pickyEaterMode: boolean;
  budgetModeEnabled: boolean;
  theme?: Theme;
}

const THEMES: { value: Theme; label: string; swatch: string }[] = [
  { value: 'emerald', label: 'Emerald', swatch: 'oklch(0.74 0.14 162)' },
  { value: 'tomato',  label: 'Tomato',  swatch: 'oklch(0.70 0.17 30)'  },
  { value: 'saffron', label: 'Saffron', swatch: 'oklch(0.80 0.15 75)'  },
  { value: 'plum',    label: 'Plum',    swatch: 'oklch(0.65 0.16 320)' },
  { value: 'cobalt',  label: 'Cobalt',  swatch: 'oklch(0.68 0.16 240)' },
];

const EQUIPMENT_OPTIONS = [
  'stovetop', 'oven', 'air_fryer', 'instant_pot', 'slow_cooker', 'microwave',
];

const SKILL_LEVELS = [
  { value: 'easy',         label: 'Easy',         desc: 'Simple recipes, minimal prep' },
  { value: 'intermediate', label: 'Intermediate', desc: 'Some technique required' },
  { value: 'expert',       label: 'Expert',       desc: 'Advanced cooking methods' },
];

const inputStyle: CSSProperties = {
  background: 'var(--surface-2)',
  border: '1px solid var(--line-soft)',
  borderRadius: 12,
  padding: '10px 14px',
  fontSize: 14,
  color: 'var(--ink)',
  fontFamily: 'inherit',
  outline: 'none',
};

function formatLabel(s: string) {
  return s.replace(/_/g, ' ');
}

function FieldLabel({ children }: { children: ReactNode }) {
  return (
    <label className="block mb-1.5" style={{ color: 'var(--ink-2)', fontSize: 13, fontWeight: 500 }}>
      {children}
    </label>
  );
}

function SectionHeader({ icon, title, hint }: { icon: ReactNode; title: string; hint?: string }) {
  return (
    <div className="mb-3">
      <div className="flex items-center gap-2" style={{ color: 'var(--ink-3)' }}>
        {icon}
        <span className="text-xs font-semibold uppercase" style={{ letterSpacing: '0.12em' }}>{title}</span>
      </div>
      {hint && <p className="text-xs mt-1" style={{ color: 'var(--ink-4)' }}>{hint}</p>}
    </div>
  );
}

function Toggle({ on, onChange, label, hint }: { on: boolean; onChange: () => void; label: string; hint?: string }) {
  return (
    <div className="flex items-center justify-between">
      <div>
        <p style={{ color: 'var(--ink)', fontSize: 14, fontWeight: 500 }}>{label}</p>
        {hint && <p className="mt-0.5" style={{ color: 'var(--ink-4)', fontSize: 12 }}>{hint}</p>}
      </div>
      <button
        type="button"
        onClick={onChange}
        role="switch"
        aria-checked={on}
        className="relative rounded-full transition-colors"
        style={{
          width: 44,
          height: 24,
          background: on ? 'var(--accent)' : 'var(--surface-3)',
          border: '1px solid var(--line-soft)',
        }}
      >
        <span
          className="absolute rounded-full transition-transform"
          style={{
            top: 2,
            left: 2,
            width: 18,
            height: 18,
            background: on ? 'var(--accent-ink)' : 'var(--ink-3)',
            transform: on ? 'translateX(20px)' : 'translateX(0)',
            boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
          }}
        />
      </button>
    </div>
  );
}

export function PreferencesPage() {
  const qc = useQueryClient();
  const { theme: currentTheme, setTheme, darkMode, toggleDark } = useTheme();
  const { data, isLoading } = useQuery<ProfileData>({
    queryKey: ['profile'],
    queryFn: () => api.get('/api/profile'),
  });

  const [familySize, setFamilySize] = useState(2);
  const [budget, setBudget] = useState('');
  const [zipCode, setZipCode] = useState('');
  const [dietaryRestrictions, setDietaryRestrictions] = useState<string[]>([]);
  const [cuisinePreferences, setCuisinePreferences] = useState<string[]>([]);
  const [equipment, setEquipment] = useState<string[]>([]);
  const [skillLevel, setSkillLevel] = useState('easy');
  const [busyNightMode, setBusyNightMode] = useState(false);
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [useFamilyRoster, setUseFamilyRoster] = useState(false);
  const [hiddenVeggieMode, setHiddenVeggieMode] = useState(false);
  const [pickyEaterMode, setPickyEaterMode] = useState(false);
  const [budgetModeEnabled, setBudgetModeEnabled] = useState(true);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (data) {
      setFamilySize(data.familySize);
      setBudget(data.budget != null ? String(data.budget) : '');
      setZipCode(data.zipCode ?? '');
      setDietaryRestrictions(data.dietaryRestrictions);
      setCuisinePreferences(data.cuisinePreferences);
      setEquipment(data.equipment ?? []);
      setSkillLevel(data.skillLevel ?? 'easy');
      setBusyNightMode(data.busyNightMode ?? false);
      setFamilyMembers(data.familyMembers ?? []);
      setUseFamilyRoster(!!data.familyMembers?.length);
      setHiddenVeggieMode(data.hiddenVeggieMode ?? false);
      setPickyEaterMode(data.pickyEaterMode ?? false);
      setBudgetModeEnabled(data.budgetModeEnabled ?? true);
    }
  }, [data]);

  const save = useMutation({
    mutationFn: (payload: ProfileData) => api.put('/api/profile', payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['profile'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
    onError: (err: any) => setError(err.body?.error ?? 'Failed to save.'),
  });

  function toggleArr(arr: string[], setArr: (v: string[]) => void, value: string) {
    setArr(arr.includes(value) ? arr.filter((x) => x !== value) : [...arr, value]);
  }

  function pickTheme(t: Theme) {
    setTheme(t);
    api.patch('/api/profile/settings', { theme: t }).catch(() => { /* non-fatal */ });
  }

  function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError('');
    const rosterActive = useFamilyRoster && familyMembers.length > 0;
    save.mutate({
      familySize: rosterActive ? familyMembers.length : familySize,
      budget: budget ? parseFloat(budget) : 200,
      dietaryRestrictions,
      cuisinePreferences,
      zipCode: zipCode || '10001',
      equipment,
      skillLevel,
      busyNightMode,
      familyMembers: rosterActive ? familyMembers : null,
      hiddenVeggieMode,
      pickyEaterMode,
      budgetModeEnabled,
      theme: currentTheme,
    } as any);
  }

  if (isLoading) {
    return (
      <Layout>
        <div className="w-full mx-auto" style={{ maxWidth: 1280, padding: '32px 24px 56px' }}>
          <p className="text-sm" style={{ color: 'var(--ink-3)' }}>Loading…</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="w-full mx-auto" style={{ maxWidth: 1280, padding: '32px 24px 56px' }}>

        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className="mb-8" style={{ maxWidth: 720 }}>
          <div className="text-xs font-semibold uppercase tracking-wider mb-2"
            style={{ color: 'var(--accent)', letterSpacing: '0.14em' }}>
            <Icon name="settings" size={12} style={{ display: 'inline', marginRight: 6, marginBottom: -1 }} />
            Preferences
          </div>
          <h1 className="h-display" style={{ fontSize: 'clamp(36px, 6vw, 56px)', margin: 0, color: 'var(--ink)', lineHeight: 1.0 }}>
            How you{' '}
            <span style={{ color: 'var(--accent)', fontStyle: 'italic', fontWeight: 400 }}>eat.</span>
          </h1>
          <p className="text-sm mt-3" style={{ color: 'var(--ink-3)' }}>
            Tweak any of these and the next plan we build will reflect them.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6" style={{ maxWidth: 720 }}>

          {/* ── Household basics ────────────────────────────────────── */}
          <div className="card p-6">
            <SectionHeader
              icon={<Icon name="family" size={14} />}
              title="Household basics"
            />
            <div className={`grid grid-cols-1 gap-4 ${useFamilyRoster ? 'sm:grid-cols-2' : 'sm:grid-cols-3'}`}>
              {!useFamilyRoster && (
                <div>
                  <FieldLabel>Family size</FieldLabel>
                  <input
                    type="number" min={1} max={20}
                    value={familySize} onChange={(e) => setFamilySize(parseInt(e.target.value, 10))}
                    style={{ ...inputStyle, width: '100%' }}
                  />
                </div>
              )}
              <div>
                <FieldLabel>Weekly budget ($)</FieldLabel>
                <input
                  type="number" min={10} max={10000} step={10}
                  value={budget} onChange={(e) => setBudget(e.target.value)} placeholder="200"
                  style={{ ...inputStyle, width: '100%' }}
                />
              </div>
              <div>
                <FieldLabel>ZIP code</FieldLabel>
                <input
                  type="text" pattern="\d{5}" maxLength={5}
                  value={zipCode} onChange={(e) => setZipCode(e.target.value)} placeholder="10001"
                  style={{ ...inputStyle, width: '100%' }}
                />
              </div>
            </div>

            <div className="mt-5 pt-5" style={{ borderTop: '1px solid var(--line-soft)' }}>
              <FamilyRosterField
                enabled={useFamilyRoster}
                onEnabledChange={setUseFamilyRoster}
                members={familyMembers}
                onMembersChange={setFamilyMembers}
              />
            </div>
          </div>

          {/* ── Dietary restrictions ────────────────────────────────── */}
          <div className="card p-6">
            <SectionHeader
              icon={<Icon name="leaf" size={14} />}
              title="Dietary restrictions"
              hint="Selected options shape every plan."
            />
            <div className="flex flex-wrap gap-2">
              {DIETARY_RESTRICTION_OPTIONS.map((opt) => (
                <Chip key={opt} on={dietaryRestrictions.includes(opt)}
                  onClick={() => toggleArr(dietaryRestrictions, setDietaryRestrictions, opt)}>
                  {formatLabel(opt)}
                </Chip>
              ))}
            </div>
          </div>

          {/* ── Cuisine preferences ─────────────────────────────────── */}
          <div className="card p-6">
            <SectionHeader
              icon={<Icon name="plate" size={14} />}
              title="Cuisine preferences"
              hint="We'll weight suggestions toward what you pick."
            />
            <div className="flex flex-wrap gap-2">
              {CUISINE_OPTIONS.map((opt) => (
                <Chip key={opt} on={cuisinePreferences.includes(opt)}
                  onClick={() => toggleArr(cuisinePreferences, setCuisinePreferences, opt)}>
                  {formatLabel(opt)}
                </Chip>
              ))}
            </div>
          </div>

          {/* ── Equipment + skill level ─────────────────────────────── */}
          <div className="card p-6">
            <SectionHeader
              icon={<Icon name="cookpot" size={14} />}
              title="Kitchen"
              hint="What you've got and how comfortable you are with it."
            />

            <p className="mb-2 mt-1" style={{ color: 'var(--ink-2)', fontSize: 13, fontWeight: 500 }}>Equipment</p>
            <div className="flex flex-wrap gap-2 mb-6">
              {EQUIPMENT_OPTIONS.map((opt) => (
                <Chip key={opt} on={equipment.includes(opt)}
                  onClick={() => toggleArr(equipment, setEquipment, opt)}>
                  {formatLabel(opt)}
                </Chip>
              ))}
            </div>

            <p className="mb-2" style={{ color: 'var(--ink-2)', fontSize: 13, fontWeight: 500 }}>Skill level</p>
            <div className="grid grid-cols-3 gap-2">
              {SKILL_LEVELS.map((s) => {
                const active = skillLevel === s.value;
                return (
                  <button key={s.value} type="button"
                    onClick={() => setSkillLevel(s.value)}
                    className="text-left transition-all"
                    style={{
                      borderRadius: 14,
                      border: `1.5px solid ${active ? 'var(--accent)' : 'var(--line-soft)'}`,
                      background: active ? 'var(--accent-soft)' : 'var(--surface-2)',
                      padding: '10px 12px',
                      cursor: 'pointer',
                    }}
                  >
                    <p style={{ color: 'var(--ink)', fontSize: 12.5, fontWeight: 600 }}>{s.label}</p>
                    <p className="mt-0.5" style={{ color: 'var(--ink-4)', fontSize: 10.5 }}>{s.desc}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* ── Behavior toggles ────────────────────────────────────── */}
          <div className="card p-6 space-y-5">
            <SectionHeader
              icon={<Icon name="sparkle" size={14} />}
              title="Cooking style"
            />
            <Toggle on={busyNightMode} onChange={() => setBusyNightMode((v) => !v)}
              label="Busy night mode" hint="Prefer recipes ≤20 minutes" />

            <Toggle on={budgetModeEnabled} onChange={() => setBudgetModeEnabled((v) => !v)}
              label="Budget mode" hint="Automatically swap expensive meals to fit your budget" />

            {(() => {
              const youngChildren = (useFamilyRoster ? familyMembers : []).filter(
                (m) => m.age >= 1 && m.age <= 12,
              );
              const youngChildNames = youngChildren
                .map((m) => (m.label && m.label.trim()) || `age ${m.age}`)
                .join(', ');
              const veggieHint = youngChildren.length > 0
                ? `Prefer recipes that sneak in vegetables — active for ${youngChildNames}`
                : 'Prefer recipes that sneak in vegetables';
              const ages = youngChildren.map((m) => m.age).join(', ');
              const pickyHint = youngChildren.length > 0
                ? `Prefer kid-friendly, familiar recipes — suggested for ages ${ages}`
                : 'Prefer kid-friendly, familiar recipes';

              return (
                <>
                  <div>
                    <Toggle on={hiddenVeggieMode} onChange={() => setHiddenVeggieMode((v) => !v)}
                      label="Hidden veggie mode" hint={veggieHint} />
                    {youngChildren.length > 0 && !hiddenVeggieMode && (
                      <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>
                        You have young children in your roster — this mode helps sneak veggies into familiar dishes.
                      </p>
                    )}
                  </div>
                  <div>
                    <Toggle on={pickyEaterMode} onChange={() => setPickyEaterMode((v) => !v)}
                      label="Picky eater mode" hint={pickyHint} />
                    {youngChildren.length > 0 && !pickyEaterMode && (
                      <p className="text-xs mt-1" style={{ color: 'var(--accent)' }}>
                        Picky eater mode prioritizes recipes with familiar flavors for younger palates.
                      </p>
                    )}
                  </div>
                </>
              );
            })()}
          </div>

          {/* ── Allergen tracker ────────────────────────────────────── */}
          <AllergenTrackerSection />

          {/* ── Appearance ──────────────────────────────────────────── */}
          <div className="card p-6">
            <SectionHeader
              icon={<Icon name="sparkle" size={14} />}
              title="Appearance"
              hint="Accent color and dark mode. Changes apply right away."
            />

            <p className="mb-2 mt-1" style={{ color: 'var(--ink-2)', fontSize: 13, fontWeight: 500 }}>Accent color</p>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-6">
              {THEMES.map((t) => {
                const active = currentTheme === t.value;
                return (
                  <button key={t.value} type="button"
                    onClick={() => pickTheme(t.value)}
                    className="flex items-center gap-2 transition-all"
                    style={{
                      borderRadius: 12,
                      border: `1.5px solid ${active ? t.swatch : 'var(--line-soft)'}`,
                      background: 'var(--surface-2)',
                      padding: '10px 12px',
                      cursor: 'pointer',
                    }}
                  >
                    <span
                      className="rounded-full flex-shrink-0"
                      style={{
                        width: 20, height: 20,
                        background: t.swatch,
                        boxShadow: active ? `0 0 0 3px color-mix(in oklab, ${t.swatch} 25%, transparent)` : undefined,
                      }}
                    />
                    <span style={{ fontSize: 12.5, fontWeight: 500, color: 'var(--ink)' }}>{t.label}</span>
                    {active && (
                      <span className="ml-auto inline-flex items-center" style={{ color: t.swatch }}>
                        <Icon name="check" size={12} stroke={2.6} />
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <Toggle
              on={darkMode}
              onChange={toggleDark}
              label="Dark mode"
              hint="Warm dark UI. Toggle anytime — also lives in the top bar."
            />
          </div>

          {/* ── Save row ────────────────────────────────────────────── */}
          <div className="flex items-center gap-4 pt-2">
            <Button type="submit" disabled={save.isPending}>
              {save.isPending ? 'Saving…' : 'Save preferences'}
            </Button>
            {error && <p className="text-sm" style={{ color: 'var(--danger)' }}>{error}</p>}
            {saved && (
              <p className="text-sm inline-flex items-center gap-1.5" style={{ color: 'var(--accent)' }}>
                <Icon name="check" size={14} stroke={2.4} />
                Preferences saved.
              </p>
            )}
          </div>
        </form>
      </div>
    </Layout>
  );
}
